import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMountedState } from 'react-use';

import { navigationBlocker } from '@mssgme/services';

const GUID = {
    id: 0,
};

export const useNavigationGuard = ({ blocked, forbid }) => {
    const history = useHistory();
    const [callback, setCallback] = useState(null);
    const [wasBlocking, setWasBlocking] = useState(false);
    const [uid] = useState(() => `blocker-${++GUID.id}`);
    const isMounted = useMountedState();
    const isBlocking = !!callback;

    useEffect(() => {
        if (!blocked) {
            return;
        }

        navigationBlocker
            .subscribe(history, uid, (callback) => {
                if (isMounted()) {
                    // '() => callback' to circumvent 'setCallback' updater treating 'callback' as a lazy initializer
                    setCallback(() => callback);
                    setWasBlocking(true);
                } else {
                    navigationBlocker.unsubscribe(uid);
                }
            })
            .when(forbid);

        return () => {
            navigationBlocker.unsubscribe(uid);
        };
    }, [uid, blocked, forbid]);

    const confirmed = useCallback(
        (allow) => {
            callback(allow);

            if (allow) {
                navigationBlocker.unsubscribe(uid);
            }

            if (isMounted()) {
                setCallback(null);

                if (!allow) {
                    setWasBlocking(false);
                }
            }
        },
        [callback]
    );

    const cancel = useCallback(() => confirmed(false), [confirmed]);
    const leave = useCallback(() => confirmed(true), [confirmed]);

    return {
        isBlocking,
        wasBlocking,
        uid,
        cancel,
        leave,
    };
};
